import React from 'react';
import { resolveUploadUrl } from '../utils/siteConfig';

export interface ClientLogosCarouselProps {
  logos: Array<{ name: string; image: string }>;
  title?: string;
}

export const ClientLogosCarousel: React.FC<ClientLogosCarouselProps> = ({ logos, title = "Trusted by Innovative Companies" }) => {
  if (!logos || logos.length === 0) {
    return null;
  }

  // Split logos into two alternating groups
  const row1LogosRaw = logos.filter((_, i) => i % 2 === 0);
  const row2LogosRaw = logos.filter((_, i) => i % 2 !== 0);

  const prepareLogos = (rawLogos: typeof logos) => {
    if (!rawLogos || rawLogos.length === 0) return [];
    let base = [...rawLogos];
    while (base.length < 8) {
      base = [...base, ...rawLogos];
    }
    return [...base, ...base];
  };

  const infiniteLogosRow1 = prepareLogos(row1LogosRaw);
  const infiniteLogosRow2 = prepareLogos(row2LogosRaw.length > 0 ? row2LogosRaw : row1LogosRaw);

  return (
    <section className="py-12 bg-white overflow-hidden border-b border-slate-100">
      <style>{`
        .logos-carousel-track {
          display: flex;
          width: max-content;
        }
        
        .scroll-left {
          animation: scrollLeft 35s linear infinite;
        }
        
        .scroll-right {
          animation: scrollRight 35s linear infinite;
        }

        @keyframes scrollLeft {
          from { transform: translateX(0); }
          to { transform: translateX(-50%); }
        }

        @keyframes scrollRight {
          from { transform: translateX(-50%); }
          to { transform: translateX(0); }
        }

        .logo-item {
          width: 160px;
          height: 70px;
          margin-right: 3rem; /* Using margin instead of flex gap ensures precise 50% translation */
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.3s ease;
        }
        
        .logo-item:hover {
          transform: scale(1.05);
        }
        
        .logo-item img {
          max-width: 100%;
          max-height: 100%;
          object-fit: contain;
        }
        
        .logo-item .fallback-name {
          font-weight: 700;
          font-size: 1.25rem;
          color: #94a3b8; /* slate-400 */
          text-align: center;
          white-space: nowrap;
        }
      `}</style>

      <div className="max-w-7xl mx-auto px-4 mb-10 text-center">
        <p className="text-sm font-bold uppercase tracking-widest text-primary">
          {title}
        </p>
      </div>

      <div className="flex flex-col gap-8">
        {/* Row 1: Right to Left */}
        <div className="w-full relative overflow-hidden group">
          <div className="absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-white to-transparent z-10 pointer-events-none" />
          <div className="absolute inset-y-0 right-0 w-24 bg-gradient-to-l from-white to-transparent z-10 pointer-events-none" />
          
          <div className="logos-carousel-track scroll-left group-hover:[animation-play-state:paused]">
            {infiniteLogosRow1.map((logo, idx) => (
              <div key={`row1-${idx}`} className="logo-item shrink-0">
                {logo.image ? (
                  <img src={resolveUploadUrl(logo.image)} alt={logo.name} title={logo.name} />
                ) : (
                  <div className="fallback-name" title={logo.name}>{logo.name}</div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Row 2: Left to Right */}
        <div className="w-full relative overflow-hidden group">
          <div className="absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-white to-transparent z-10 pointer-events-none" />
          <div className="absolute inset-y-0 right-0 w-24 bg-gradient-to-l from-white to-transparent z-10 pointer-events-none" />
          
          <div className="logos-carousel-track scroll-right group-hover:[animation-play-state:paused]">
            {infiniteLogosRow2.map((logo, idx) => (
              <div key={`row2-${idx}`} className="logo-item shrink-0">
                {logo.image ? (
                  <img src={resolveUploadUrl(logo.image)} alt={logo.name} title={logo.name} />
                ) : (
                  <div className="fallback-name" title={logo.name}>{logo.name}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
